/**
 * talentContext.js
 *
 * Pure, framework-free helper that converts a list of engineer profiles into a
 * compact, voice-friendly Dutch context string suitable for injection into an
 * ElevenLabs voice-agent session via sendContextualUpdate.
 *
 * Exported:
 *   buildTalentContext(engineers) → string
 */

const HEADER =
  'Beschikbare professionals voor matching (gebruik deze namen en gegevens, verzin niets):';

const SUMMARY_MAX = 140;
const SKILLS_MAX = 5;

/**
 * Truncate a string to at most `max` characters, appending '…' when cut.
 * @param {string} str
 * @param {number} max
 * @returns {string}
 */
function truncate(str, max) {
  if (!str) return '';
  if (str.length <= max) return str;
  return str.slice(0, max).trimEnd() + '…';
}

/**
 * Format one engineer profile as a single compact line.
 * @param {object} eng
 * @returns {string}
 */
function formatEngineer(eng) {
  const name = eng.name || 'Onbekend';
  const role = eng.role || 'Onbekende rol';

  const skills = Array.isArray(eng.skills) && eng.skills.length > 0
    ? eng.skills.slice(0, SKILLS_MAX).join(', ')
    : 'geen skills';

  let availability;
  if (eng.beschikbaarheid && eng.beschikbaarheid.status) {
    availability = eng.beschikbaarheid.status;
  } else {
    availability = 'beschikbaarheid onbekend';
  }

  const summary = eng.dossier?.profileSummary
    ? truncate(eng.dossier.profileSummary, SUMMARY_MAX)
    : '';

  const parts = [`${name} — ${role} · ${skills} · ${availability}`];
  if (summary) parts.push(summary);

  return parts.join(' · ');
}

/**
 * Build a compact, voice-friendly Dutch context string from an array of
 * engineer profiles. Handles null/undefined input and empty arrays gracefully.
 *
 * @param {Array|null|undefined} engineers
 * @returns {string}
 */
export function buildTalentContext(engineers) {
  if (!Array.isArray(engineers) || engineers.length === 0) {
    return 'Geen profielen geladen — geen profielen geladen voor deze sessie.';
  }

  const lines = engineers.map(formatEngineer);
  return [HEADER, ...lines].join('\n');
}
